// @flow
import {fromJS, Map, List} from 'immutable';
import moment from 'moment';
import {Store, setUpStore} from 'client/framework';
import appStore from 'client/AppStore';
import makeConstants from 'lib/makeConstants';
import {type QueryBuilder, resource} from '@albert-io/json-api-framework/request/builder';
import type {ClassroomModelV1} from 'resources/augmented/Classroom/ClassroomModel.v1';
import {SubjectModelV2} from 'resources/GeneratedModels/Subject/SubjectModel.v2';
import {AssignmentModelV3} from 'resources/augmented/Assignment/AssignmentModel.v3';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import classroomStore from 'client/Classrooms/Classroom/ClassroomV2.store';
import SelectableDateRangeStore from 'generic/SelectableDateRange/SelectableDateRange.store';
import {RANGES} from 'generic/SelectableDateRange/shared';

import classroomInsightsActions from './ClassroomInsights.actions';

const reportTypes = makeConstants('subject', 'assignment', 'assessment');

class ClassroomInsightsStore extends Store {
  constructor(name: string) {
    super(name);
    this.setInitialData(
      fromJS({
        classroomId: '',
        filterOptions: {},
        subjectId: '',
        guideLevelId: '',
        attemptNumber: 'mostRecent',
        startDate: null,
        endDate: null,
        reportType: 'subject',
        assignment: null,
        assessment: null
      })
    );

    this.selectableDateRangeStore = setUpStore(
      SelectableDateRangeStore,
      'ClassInsightsSelectableDateRangeStore'
    );
    this.handle(classroomInsightsActions.SET_REPORT_TYPE, this.setProperty('reportType'));
    this.handle(classroomInsightsActions.SET_ATTEMPT_NUMBER, this.setProperty('attemptNumber'));
    this.handle(classroomInsightsActions.SET_CLASSROOM_ID, this.setClassroomId);
    this.handle(classroomInsightsActions.SET_GUIDE_LEVEL, this.setGuideLevel);
    this.handle(classroomInsightsActions.SET_ASSIGNMENT, this.setProperty('assignment'));
    this.handle(classroomInsightsActions.SET_ASSESSMENT, this.setProperty('assessment'));
  }

  setClassroomId(classroomId: string) {
    const currentClassroomId = this.readData('classroomId');

    if (classroomId !== currentClassroomId) {
      this.resetStore();
      this.writeData('classroomId', classroomId);
    }
  }

  setGuideLevel({subjectId, guideLevelId}: {subjectId: string, guideLevelId: string}) {
    this.writeData((store) => store.set('subjectId', subjectId).set('guideLevelId', guideLevelId));
  }

  getClassroomSubjectGuides(): List<SubjectModelV2> {
    const guides = resource('subjects_v2')
      .mandarkEndpoint(['subjects_v2'])
      .include('guides_v1.guide_levels_v2')
      .filter({
        classrooms_v1: appStore.routerProps.params.classId,
        included: {
          guides_v1: {
            guide_type: 'practice'
          }
        }
      })
      .withMeta('guide_v1');

    return guides.isResourcePopulated() ? guides.getResource() : List();
  }

  getClassroomAssignments(type): QueryBuilder {
    const assignmentType = this.isAssignmentReport() ? 'assignment' : 'exam';
    return resource('assignments_v3')
      .mandarkEndpoint(['assignments_v3'])
      .filter({
        assignment_type: type || assignmentType,
        classrooms_v1: appStore.routerProps.params.classId
      })
      .pageSize(250)
      .sort('name');
  }

  getClassroomInsightsQuery(): QueryBuilder {
    const isSubjectReport = this.isSubjectReport();
    return resource('classrooms_v1')
      .mandarkEndpoint(['classrooms_v1', appStore.routerProps.params.classId])
      .withMeta('classroom_v1')
      .meta({
        context: {
          classroom: {
            insights: true
          }
        }
      })
      .meta(
        {
          context: {
            start_date: this.getStartDate(),
            end_date: this.getEndDate()
          }
        },
        isSubjectReport && Boolean(this.getStartDate()) && Boolean(this.getEndDate())
      )
      .meta(
        {
          context: {
            subject: {
              id: this.getSubjectId()
            },
            guess: {
              most_recent: this.isMostRecentAttempt(),
              max_attempt: true
            }
          }
        },
        isSubjectReport
      )
      .meta(
        {
          context: {
            assignment: {
              id: this.getAssignmentId()
            }
          }
        },
        !isSubjectReport
      )
      .meta(
        {
          context: {
            guess: {
              attempt_number: this.getAttemptNumber()
            }
          }
        },
        !this.isMostRecentAttempt()
      )
      .meta(
        {
          context: {
            guide_level: {
              id: this.getGuideLevelId()
            }
          }
        },
        Boolean(this.getGuideLevelId() && isSubjectReport)
      );
  }

  // Student Performance Tables

  getStudentPerformanceQuery(): QueryBuilder {
    const isSubjectReport = this.isSubjectReport();
    const query = resource('students_v2')
      .mandarkEndpoint(['classrooms_v1', appStore.routerProps.params.classId, 'students_v2'])
      .withMeta('student_v2')
      .meta(
        {
          context: {
            subject: {
              ids: [this.getSubjectId()]
            }
          }
        },
        isSubjectReport
      );

    return this.getPerformanceMetaContext(query);
  }

  getStudentsSearchFilters(query: QueryBuilder, term: string): QueryBuilder {
    return query.filter({
      any_of: [
        {
          first_name: {
            case_insensitive_substring: term
          }
        },
        {
          last_name: {
            case_insensitive_substring: term
          }
        }
      ]
    });
  }

  getStudentsPerformanceCSVQuery = (headers: string): QueryBuilder => {
    const isSubjectReport = this.isSubjectReport();
    return resource('students_v2')
      .mandarkEndpoint(['classrooms_v1', appStore.routerProps.params.classId, 'students_v2'])
      .withMeta('student_v2')
      .customHeader({Accept: 'text/csv'})
      .csv({
        context: {
          classroom: {
            insights: true
          },
          csv_headers: headers
        }
      })
      .csv(
        {
          context: {
            assignment: {
              id: this.getAssignmentId()
            }
          }
        },
        !isSubjectReport
      )
      .csv(
        {
          context: {
            start_date: this.getStartDate(),
            end_date: this.getEndDate(),
            guess: this.isMostRecentAttempt()
              ? {most_recent: true}
              : {attempt_number: this.getAttemptNumber()}
          }
        },
        isSubjectReport
      )
      .csv(
        {
          context: {
            subject: {
              ids: [this.getSubjectId()]
            }
          }
        },
        isSubjectReport
      )
      .csv(
        {
          context: {
            guide_level: {
              id: this.getGuideLevelId()
            }
          }
        },
        Boolean(this.getGuideLevelId() && isSubjectReport)
      )
      .done();
  };

  // Questions Performance Tables

  getQuestionsPerformanceQuery(): QueryBuilder {
    const isSubjectReport = this.isSubjectReport();
    const query = resource('questions_v2')
      .mandarkEndpoint(['questions_v2'])
      .include('question_set_v1')
      .withMeta('question_v2')
      .filter(
        {
          question_set_v1: {
            category: {
              not: 'exam'
            },
            subject_v2: this.getSubjectId()
          }
        },
        isSubjectReport
      )
      .filter(
        {
          question_set_v1: {
            assignments_v3: this.getAssignmentId()
          }
        },
        Boolean(this.getAssignmentId()) && !isSubjectReport
      )
      .filter(
        {
          question_set_v1: {
            guide_levels_v2: this.getGuideLevelId()
          }
        },
        Boolean(this.getGuideLevelId() && isSubjectReport)
      )
      .filter(
        {
          question_set_v1: {
            guide_levels_v2: {
              nlevel: 1
            }
          }
        },
        !this.getGuideLevelId() && isSubjectReport
      )
      .meta({
        context: {
          classroom: {
            id: appStore.routerProps.params.classId,
            insights: true
          }
        }
      });

    return this.getPerformanceMetaContext(query);
  }

  getQuestionsSearchFilters(query: QueryBuilder, term: string): QueryBuilder {
    return query.filter({
      title: {
        case_insensitive_substring: term
      }
    });
  }

  getQuestionsPerformanceCSVQuery = (headers: string): QueryBuilder => {
    const isSubjectReport = this.isSubjectReport();
    return resource('questions_v2')
      .mandarkEndpoint(['questions_v2'])
      .withMeta('question_v2')
      .customHeader({Accept: 'text/csv'})
      .filter(
        {
          question_set_v1: {
            category: {
              not: 'exam'
            },
            subject_v2: this.getSubjectId()
          }
        },
        isSubjectReport
      )
      .filter(
        {
          question_set_v1: {
            assignments_v3: this.getAssignmentId()
          }
        },
        Boolean(this.getAssignmentId()) && !isSubjectReport
      )
      .filter(
        {
          question_set_v1: {
            guide_levels_v2: this.getGuideLevelId()
          }
        },
        Boolean(this.getGuideLevelId()) && isSubjectReport
      )
      .filter(
        {
          question_set_v1: {
            guide_levels_v2: {
              nlevel: 1
            }
          }
        },
        !this.getGuideLevelId() && isSubjectReport
      )
      .csv({
        context: {
          classroom: {
            id: appStore.routerProps.params.classId,
            insights: true
          },
          csv_headers: headers
        }
      })
      .csv(
        {
          context: {
            assignment: {
              id: this.getAssignmentId()
            }
          }
        },
        !isSubjectReport
      )
      .csv(
        {
          context: {
            start_date: this.getStartDate(),
            end_date: this.getEndDate(),
            guess: this.isMostRecentAttempt()
              ? {most_recent: true}
              : {attempt_number: this.getAttemptNumber()}
          }
        },
        isSubjectReport
      )
      .done();
  };

  // Standards Performance Tables
  getStandardsPerformanceQuery(): QueryBuilder {
    const isSubjectReport = this.isSubjectReport();
    const query = resource('labels_v1')
      .mandarkEndpoint(['labels_v1'])
      .include('subject_v2')
      .filter({
        'meta.count_of_classroom_questions': {
          not: 0
        }
      })
      .filter(
        {
          subject_v2: {
            id: this.getSubjectId()
          }
        },
        isSubjectReport
      )
      .filter(
        {
          subject_v2: {
            guide_levels_v2: this.getGuideLevelId()
          }
        },
        Boolean(this.getGuideLevelId()) && isSubjectReport
      )
      .filter(
        {
          questions_v3: {
            question_set_v1: {
              assignments_v3: this.getAssignmentId()
            }
          }
        },
        !isSubjectReport
      )
      .withMeta('label_v1')
      .meta({
        context: {
          label: {
            counts: true
          },
          classroom: {
            id: appStore.routerProps.params.classId
          }
        }
      })
      .meta(
        {
          context: {
            subject: {
              id: this.getSubjectId()
            }
          }
        },
        isSubjectReport
      );

    return this.getPerformanceMetaContext(query);
  }

  getStandardsSearchFilters(query: QueryBuilder, term: string): QueryBuilder {
    return query.filter({
      any_of: [
        {
          name: {
            case_insensitive_substring: term
          }
        },
        {
          category: {
            case_insensitive_substring: term
          }
        }
      ]
    });
  }

  getStandardsPerformanceCSVQuery = (headers: string): QueryBuilder => {
    const isSubjectReport = this.isSubjectReport();
    return resource('labels_v1')
      .mandarkEndpoint(['labels_v1'])
      .customHeader({Accept: 'text/csv'})
      .filter(
        {
          subject_v2: {
            id: this.getSubjectId()
          }
        },
        isSubjectReport
      )
      .filter(
        {
          subject_v2: {
            guide_levels_v2: this.getGuideLevelId()
          }
        },
        Boolean(this.getGuideLevelId()) && isSubjectReport
      )
      .filter(
        {
          questions_v3: {
            question_set_v1: {
              assignments_v3: this.getAssignmentId()
            }
          }
        },
        !isSubjectReport
      )
      .withMeta('label_v1')
      .csv({
        context: {
          classroom: {
            id: appStore.routerProps.params.classId,
            insights: true
          },
          csv_headers: headers
        }
      })
      .csv(
        {
          context: {
            assignment: {
              id: this.getAssignmentId()
            }
          }
        },
        !isSubjectReport
      )
      .csv(
        {
          context: {
            subject: {
              id: this.getSubjectId()
            },
            start_date: this.getStartDate(),
            end_date: this.getEndDate(),
            guess: this.isMostRecentAttempt()
              ? {most_recent: true}
              : {attempt_number: this.getAttemptNumber()}
          }
        },
        isSubjectReport
      )
      .done();
  };

  getPerformanceMetaContext(query: QueryBuilder): QueryBuilder {
    const isSubjectReport = this.isSubjectReport();
    const assignmentId = this.getAssignmentId();

    return query
      .meta(
        {
          context: {
            start_date: this.getStartDate(),
            end_date: this.getEndDate(),
            guess: this.isMostRecentAttempt()
              ? {most_recent: true}
              : {attempt_number: this.getAttemptNumber()}
          }
        },
        isSubjectReport
      )
      .meta(
        {
          context: {
            guide_level: {
              id: this.getGuideLevelId()
            }
          }
        },
        Boolean(this.getGuideLevelId()) && isSubjectReport
      )
      .meta(
        {
          context: {
            assignment: {
              id: assignmentId
            }
          }
        },
        Boolean(assignmentId) && !isSubjectReport
      );
  }

  getDefaultDateRange(): Map<string, *> {
    const start = classroomStore.getActiveClassroom().getBeginDate();
    return Map({
      name: RANGES.CUSTOM,
      id: RANGES.CUSTOM,
      start_date: start.format('YYYY-MM-DD'),
      end_date: moment().format('YYYY-MM-DD')
    });
  }

  getDateRangeDropdownStore(): Map<string, *> | null {
    return this.selectableDateRangeStore.getSimpleDropdownStore();
  }

  getStartDate(): number {
    const startDate = this.getDateRangeDropdownStore()
      .getSelectedItem()
      .get('start_date');
    const parsedStartDate = moment(startDate).isValid() ? moment(startDate) : moment(0);
    return parsedStartDate.startOf('day').valueOf();
  }

  getEndDate(): number {
    const endDate = this.getDateRangeDropdownStore()
      .getSelectedItem()
      .get('end_date');
    const parsedEndDate = moment(endDate).isValid() ? moment(endDate) : moment();
    return parsedEndDate.endOf('day').valueOf();
  }

  getSelectableDateRangeStoreName(): string {
    return this.selectableDateRangeStore.getName();
  }

  getSubjectId(): string {
    return this.readData('subjectId')
      ? this.readData('subjectId')
      : classroomStore
          .getClassroomSubjects()
          .first()
          .getId();
  }

  getSubject(): SubjectModelV2 {
    const guides = this.getClassroomSubjectGuides();
    const subject = guides.find((subjectGuide) => subjectGuide.getId() === this.getSubjectId());

    return subject || SubjectModelV2.getDefaultModel();
  }

  getAssignmentOrAssessment() {
    const assignments = this.getClassroomAssignments().getResource();
    const earliestAssignment = assignments
      .sortBy((assignment) => assignment.getStartDate())
      .first();
    const selectedAssignment = this.isAssignmentReport()
      ? this.readData('assignment')
      : this.readData('assessment');

    return selectedAssignment || earliestAssignment || AssignmentModelV3.getDefaultModel();
  }

  getAssignmentId() {
    return this.getAssignmentOrAssessment().getId();
  }

  getAssignmentName() {
    return this.getAssignmentOrAssessment().getName();
  }

  getGuideLevelId(): string {
    return this.readData('guideLevelId');
  }

  getAttemptNumber(): string | number {
    return this.readData('attemptNumber');
  }

  getClassroomInsights(): ClassroomModelV1 {
    return this.getClassroomInsightsQuery().getResource();
  }

  getReportType() {
    return this.readData('reportType');
  }

  areClassroomInsightsPopulated(): boolean {
    return this.getClassroomInsightsQuery().isResourcePopulated();
  }

  isMostRecentAttempt(): boolean {
    return this.getAttemptNumber() === 'mostRecent';
  }

  isSubjectReport() {
    return this.getReportType() === reportTypes.subject;
  }

  isAssignmentReport() {
    return this.getReportType() === reportTypes.assignment;
  }

  isAssessmentReport() {
    return this.getReportType() === reportTypes.assessment;
  }
}

export default new ClassroomInsightsStore('ClassroomInsightsStorev2');
